import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
	useCreateLiCompanyActivityMutation,
	useUpdateLiCompanyActivityMutation,
} from "@/pages/Private/redux/liCompanyActivity/liCompanyActivity.api";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, toastFailure, useSearch } from "@/utils";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { InputField } from "@/components/InputField/InputField";

import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";

import { LiCompanyActivityTimeframe } from "@/enum/licompanyactivity.enum";

import {
	AutoComplete,
	AutoCompleteItem as AutoCompleteItemType,
} from "@/components/AutoComplete/AutoComplete";

import { useGetProfilesWithAdminPermissionsQuery } from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import {
	CreateLiCompanyActivity,
	CreateLiCompanyActivitySchema,
	LiCompanyActivity,
} from "../schema/liCompanyActivity";
import { List } from "../../List/schema/list";

interface CreateLiCompanyActivityFormProps {
	company?: LiCompanyActivity | null;
	list: List;
	handleSelect?: (company: LiCompanyActivity | null) => void;
	handleDuplicate?: (company: LiCompanyActivity | null) => void;
}

const defaultValues: Partial<CreateLiCompanyActivity> = {
	name: "",
	url: "",
	scrapeFollowers: true,
	timeframe: null,
	amount: null,
	plannedScrapingUserId: null,
};

export const CreateLiCompanyActivityForm: FunctionComponent<CreateLiCompanyActivityFormProps> = ({
	company,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liCompanyActivity.${key}`);
	const [createLiCompanyActivity, { isLoading, error }] = useCreateLiCompanyActivityMutation();
	const [updateLiCompanyActivity, { error: updateError }] = useUpdateLiCompanyActivityMutation();

	const { query: profileQuery, setSearchValue: setProfileSearchValue } = useSearch();

	const { data: profiles } = useGetProfilesWithAdminPermissionsQuery({
		page: 1,
		limit: 20,
		searchValue: profileQuery,
	});

	const {
		handleSubmit,
		reset,
		setValue,
		getValues,
		formState: { errors, isValid },
		clearErrors,
	} = useForm<CreateLiCompanyActivity>({
		defaultValues: {
			name: company?.name || "",
			url: company?.url || "",
			scrapeFollowers: company?.scrapeFollowers || true,
			timeframe: company?.timeframe || null,
			amount: company?.amount || null,
		},
		resolver: zodResolver(CreateLiCompanyActivitySchema),
	});

	useEffect(() => {
		if (isValid) {
			clearErrors();
		}
	}, [isValid, clearErrors]);

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateLiCompanyActivity>>({});

	const handleSaveFormStateMultiple = (
		values: {
			value: CreateLiCompanyActivity[keyof CreateLiCompanyActivity];
			name: keyof CreateLiCompanyActivity;
		}[]
	) => {
		const newState = values.reduce(
			(acc, value) => {
				return {
					...acc,
					[value.name]: value.value,
				};
			},
			{ ...currentFormState }
		);

		setCurrentFormState(newState);

		const newObject = values.reduce(
			(acc, value) => {
				return {
					...acc,
					[value.name]: value.value,
				};
			},
			{ ...currentFormState }
		);

		if (newObject) {
			reset(newObject);
		}
	};

	const handleSaveFormState = (
		value: CreateLiCompanyActivity[keyof CreateLiCompanyActivity],
		name: keyof CreateLiCompanyActivity
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	useEffect(() => {
		if (company) {
			const arr = [
				{ name: "id" as keyof CreateLiCompanyActivity, value: company?.id },
				{ name: "name" as keyof CreateLiCompanyActivity, value: company?.name },
				{ name: "url" as keyof CreateLiCompanyActivity, value: company?.url },
				{
					name: "scrapeFollowers" as keyof CreateLiCompanyActivity,
					value: company.scrapeFollowers || false,
				},
				{
					name: "timeframe" as keyof CreateLiCompanyActivity,
					value: company.timeframe || null,
				},
				{
					name: "amount" as keyof CreateLiCompanyActivity,
					value: company.amount || null,
				},
				{
					name: "plannedScrapingUserId" as keyof CreateLiCompanyActivity,
					value: company.plannedScrapingUserId || null,
				},
			];

			handleSaveFormStateMultiple(arr);
		} else {
			reset();
		}
	}, [company, reset]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateLiCompanyActivity) => {
		try {
			if (company?.id && !handleDuplicate) {
				await updateLiCompanyActivity({
					...values,
					id: company.id,
					listId: list.id,
				}).unwrap();
			} else {
				await createLiCompanyActivity({
					...values,
					listId: list.id,
				}).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);
		} catch (err) {
			toastFailure(err, t("errors.saveFailed"));
		}
	};

	const buttonTitle =
		handleDuplicate && company?.id
			? t("basics.copyCriteria")
			: company?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-md mb-5">Add a source</div>
			<div className="flex flex-row gap-4 w-full mb-1">
				<InputField
					error={!!errors.name?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "name");
					}}
					label={"Source name"}
					name={"name"}
					placeholder={ts("namePlaceholder")}
					showError={!!errors.name?.message}
					value={getValues("name")}
				/>
			</div>
			<div className="flex flex-row gap-4 w-full mb-1">
				<InputField
					error={!!errors.url?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "url");
					}}
					label={ts("url")}
					name={"url"}
					placeholder={ts("urlPlaceholder")}
					showError={!!errors.url?.message}
					value={getValues("url")}
				/>
			</div>

			<div className="flex flex-row w-full mb-4">
				<AutoComplete
					data={
						profiles?.data?.map((c) => {
							return {
								id: c.id,
								title: c.firstName + " " + c.lastName,
							} as AutoCompleteItemType;
						}) || []
					}
					handleSearch={(value: string) => setProfileSearchValue(value)}
					handleSelect={async (item?: AutoCompleteItemType) => {
						await handleSaveFormState(item?.id || null, "plannedScrapingUserId");
					}}
					information="The selected user needs to have admin rights of the Linkedin company page"
					label={"Scrape followers with the following account"}
					placeholder="Leave empty for random profile"
					selectedItem={
						currentFormState.plannedScrapingUserId
							? profiles?.data.find((p) => p.id === currentFormState.plannedScrapingUserId)
									?.firstName +
								" " +
								profiles?.data.find((p) => p.id === currentFormState.plannedScrapingUserId)
									?.lastName
							: ""
					}
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-4">
				<Dropdown
					data={[
						{
							title: "only consider maximum amount",
							id: null,
						},
						{
							title: ts(LiCompanyActivityTimeframe.NOW),
							id: LiCompanyActivityTimeframe.NOW,
						},
						{
							title: ts(LiCompanyActivityTimeframe.THIS_MONTH),
							id: LiCompanyActivityTimeframe.THIS_MONTH,
						},
						{
							title: ts(LiCompanyActivityTimeframe.LAST_MONTH),
							id: LiCompanyActivityTimeframe.LAST_MONTH,
						},
						{
							title: ts(LiCompanyActivityTimeframe.LAST_3_MONTHS),
							id: LiCompanyActivityTimeframe.LAST_3_MONTHS,
						},
						{
							title: ts(LiCompanyActivityTimeframe.LAST_6_MONTHS),
							id: LiCompanyActivityTimeframe.LAST_6_MONTHS,
						},
						{
							title: ts(LiCompanyActivityTimeframe.LAST_YEAR),
							id: LiCompanyActivityTimeframe.LAST_YEAR,
						},
					]}
					defaultValue={
						currentFormState.timeframe
							? {
									title: ts(currentFormState.timeframe),
									id: currentFormState.timeframe,
								}
							: undefined
					}
					error={!!errors.timeframe?.message}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						handleSaveFormState((value?.id as LiCompanyActivityTimeframe) || null, "timeframe");
					}}
					label={ts("timeframe")}
				/>
			</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					error={!!errors.amount?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(+event.target.value, "amount");
					}}
					label={ts("amount")}
					name={"amount"}
					placeholder={ts("amountPlaceholder")}
					showError={!!errors.amount?.message}
					value={currentFormState.amount || ""}
				/>
			</div>

			{getAllErrors(error || updateError, formErrors).length ? (
				<div className="flex flex-row gap-4 w-full mb-4">
					{renderErrorMessages(getAllErrors(error || updateError, formErrors))}
				</div>
			) : null}

			<div className="flex gap-4 w-full justify-end">
				{handleSelect && (
					<div className="flex max-w-[100px] w-full">
						<Button
							className="w-full"
							color={ButtonColor.ACTION_SECONDARY}
							title="cancel"
							onClick={() => {
								handleSelect(null);
								handleDuplicate?.(null);
								reset(defaultValues);
								setCurrentFormState(defaultValues);
							}}
						/>
					</div>
				)}

				<div className="flex max-w-[150px] w-full">
					<Button
						className="w-full"
						isLoading={isLoading}
						title={buttonTitle}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>
		</div>
	);
};
